import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
    Button,
    Modal,
    ModalComponent,
    notify,
} from '@speeki/global-ui-components';
import { ModalProps } from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import { ChangePasswordDocument } from '@graphql/generated/graphql';

import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { passwordRequirements } from '@shared/passwordRequirments';
import { passwordValid } from '@shared/validationFunctions';

import { FullPasswordField, PasswordCheck } from './PasswordChangeModal.styled';

interface PasswordChangeModalProps
    extends Pick<ModalProps, 'onClose' | 'open'> {}

export const PasswordChangeModal = ({
    onClose,
    open,
}: PasswordChangeModalProps) => {
    const { t } = useTranslation();

    const {
        formState: { dirtyFields, errors, isSubmitted, isSubmitting, isValid },
        handleSubmit,
        register,
        reset,
        setError,
        watch,
    } = useForm<ChangePasswordFormValues>({
        mode: 'onChange',
        resolver: yupResolver(ChangePasswordSchema),
    });

    const [changePassword, { error, loading }] = useMutation(
        ChangePasswordDocument,
        {
            onCompleted: () => {
                reset();
                onClose();
                notify(t('PASSWORD_CHANGED')).SUCCESS();
            },
            onError: () => {},
        },
    );

    const isPasswordCheckOpened =
        dirtyFields.password || (!isValid && isSubmitted);
    const password = watch('password');

    const handleChangePassword = (data: ChangePasswordFormValues) => {
        void changePassword({
            variables: {
                input: {
                    newPassword: data.password,
                    password: data.currentPassword,
                },
            },
        });
    };
    const handleCancel = () => {
        reset();
        onClose();
    };

    useEffect(() => {
        if (error?.message) {
            setError('currentPassword', {
                message: 'WRONG_CURRENT_PASSWORD',
                type: 'invalid-password',
            });
        }
    }, [error]);

    return (
        <ModalComponent
            closeButton
            onClose={onClose}
            open={open}
            preventCloseOutside
            width={648}
        >
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('CHANGE_PASSWORD')}
                    </Modal.TitleSecondary>
                    <Modal.Description>
                        {t('SETTINGS_CHANGE_PASSWORD_TEXT')}
                    </Modal.Description>
                </Modal.Header>

                <StandardForm>
                    <FullPasswordField
                        {...register('currentPassword')}
                        className="htmlToImageIgnore"
                        error={!!errors.currentPassword}
                        label={t('CURRENT_PASSWORD')}
                        message={t(errors?.currentPassword?.message || '')}
                        name={'currentPassword'}
                        placeholder={t('CURRENT_PASSWORD_PLACEHOLDER')}
                        testId="9RcemA4"
                    />

                    <div>
                        <FullPasswordField
                            {...register('password')}
                            className="htmlToImageIgnore"
                            error={!!errors.password}
                            label={t('NEW_PASSWORD')}
                            name={'password'}
                            placeholder={t('NEW_PASSWORD_PLACEHOLDER')}
                            testId="ZsCfCls"
                        />
                        <PasswordCheck
                            isDirty={isPasswordCheckOpened}
                            isOpen={isPasswordCheckOpened}
                            isSubmitted={isSubmitted}
                            passwordValue={password}
                            requirements={passwordRequirements}
                            testId="U8mOl3l"
                        />
                    </div>

                    <FullPasswordField
                        {...register('passwordConfirmation')}
                        className="htmlToImageIgnore"
                        error={!!errors.passwordConfirmation}
                        label={t('CONFIRM_NEW_PASSWORD')}
                        message={t(errors?.passwordConfirmation?.message || '')}
                        name={'passwordConfirmation'}
                        placeholder={t('CONFIRM_NEW_PASSWORD_PLACEHOLDER')}
                        testId="g3SEet-"
                    />
                </StandardForm>
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={handleCancel}
                        testId="button_cancel"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={isSubmitting || (!isValid && isSubmitted)}
                        isLoading={loading || isSubmitting}
                        onClick={handleSubmit(handleChangePassword)}
                        testId="button_save"
                        variant={'primary'}
                    >
                        {t('SAVE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};

const ChangePasswordSchema = yup.object({
    currentPassword: yup.string().required('FIELD_CANNOT_BE_EMPTY'),
    password: yup
        .string()
        .required()
        .test('passwordValidation', '', passwordValid),
    passwordConfirmation: yup
        .string()
        .required('FIELD_CANNOT_BE_EMPTY')
        .oneOf([yup.ref('password')], 'PASSWORD_RESET_CONFIRM_DO_NOT_MATCH'),
});

type ChangePasswordFormValues = yup.InferType<typeof ChangePasswordSchema>;
